import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CIRCLE_TYPES, type CircleTypeKey } from '../../lib/circleCapabilities'
import { useActionBoundary } from '../../hooks/useActionBoundary'
import { useCreateCircle } from '../../hooks/useCircles'
import { useToast } from '../../contexts/ToastContext'
import { Modal } from '../ui/Modal'
import { GatedButton } from '../ui/GatedButton'
import { cn } from '../../lib/cn'

interface NewCircleModalProps {
  open: boolean
  onClose: () => void
}

/**
 * New-circle modal (COTC_Circles R6, DESIGN §3.14). Three type options, taglines, and an
 * immutable-type warning. Create → POST /circles, navigate to the new circle, toast.
 */
export function NewCircleModal({ open, onClose }: NewCircleModalProps) {
  const [selected, setSelected] = useState<CircleTypeKey | null>(null)
  const navigate = useNavigate()
  const { flash } = useToast()
  const create = useCreateCircle()
  const capture = useActionBoundary('new-circle')
  const inFlight = useRef(false)

  const chosen = CIRCLE_TYPES.find((t) => t.key === selected) ?? null

  function reset() {
    setSelected(null)
  }

  function close() {
    if (inFlight.current || create.isPending) return
    reset()
    onClose()
  }

  async function submit() {
    if (!chosen || inFlight.current || create.isPending) return
    inFlight.current = true
    const current = capture()
    try {
      const circle = await create.mutateAsync({ type: chosen.key })
      if (!current()) return
      flash(`${chosen.label} started — invite a family to fill it.`)
      reset()
      onClose()
      navigate(`/circles/${circle.id}`)
    } catch {
      if (current()) flash('That could not be created. Please try again.')
    } finally {
      inFlight.current = false
    }
  }

  return (
    <Modal open={open} onClose={close} title="What kind of circle?">
      <p className="mb-5 text-[13.5px] leading-relaxed text-ink-muted">
        The type decides what members can see about each other. You cannot change it later — start
        another circle instead.
      </p>

      <div className="flex flex-col gap-[10px]">
        {CIRCLE_TYPES.map((t) => {
          const active = selected === t.key
          return (
            <button
              key={t.key}
              type="button"
              aria-pressed={active}
              disabled={create.isPending}
              onClick={() => { if (!inFlight.current) setSelected(t.key) }}
              className={cn(
                'flex items-start gap-3 rounded-card border px-4 py-[14px] text-left transition-colors',
                active
                  ? 'border-terra bg-paper-row'
                  : 'border-line-soft bg-paper-surface hover:border-terra/60',
              )}
            >
              <span
                className="mt-[5px] h-[11px] w-[11px] flex-none rounded-full"
                style={{ background: t.dot }}
              />
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold text-ink">{t.label}</span>
                <span className="mt-1 block text-[12.5px] leading-relaxed text-ink-muted">{t.tagline}</span>
              </span>
            </button>
          )
        })}
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={close}
          disabled={create.isPending}
          className="rounded-btn px-4 py-3 text-[13.5px] font-medium text-ink-muted hover:text-terra-deep"
        >
          Cancel
        </button>
        <GatedButton
          gated={!chosen || create.isPending}
          disabled={create.isPending}
          hint="Pick a circle type to start one."
          variant="accent"
          onClick={submit}
        >
          {create.isPending ? 'Starting…' : 'Start circle'}
        </GatedButton>
      </div>
    </Modal>
  )
}
